import {
  AddCircleOutline,
  AttachMoney as AttachMoneyIcon,
  BookmarkBorder as BookmarkIcon,
  Business as BusinessIcon,
  CheckBox as CheckBoxIcon,
  ChevronRight as ChevronRightIcon,
  CreditCard as CreditCardIcon,
  Description as DescriptionIcon,
  Home as HomeIcon,
  Inventory as InventoryIcon,
  People as PeopleIcon,
  PersonAdd as PersonAddIcon,
  Receipt as ReceiptIcon,
  Search,
  Warning as WarningIcon,
} from "@mui/icons-material";
import {
  Box,
  Card,
  CardActionArea,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";

import { memo, useCallback, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import FlxGrid from "../../../../_src_shared/components/layout/FlxGrid";
import { loanDrilldownSidebarTypes } from "../../../../constants/enums/loanDrilldownScreenSidebarOptions";
import {
  sidebarSetStateValue,
  sidebarTypeSet,
} from "../../../../redux/actions/sidebarActions";
import DrilldownSidebarPane from "../DrilldownSidebarPane";

const DrilldownCreateNewItemSidebar = memo(() => {
  const dispatch = useDispatch();
  const onSelect = useCallback(
    (d) => {
      // console.log("D", d);
      dispatch(sidebarTypeSet(d));
      dispatch(sidebarSetStateValue(null));
      // setFileComments(null);
    },
    [dispatch],
  );

  return (
    <>
      <DrilldownSidebarPane
        title="Add To Loan"
        // bodyPadding={0}
        icon={<AddCircleOutline className="thin-8" />}
        headerSx={{ pb: 1.5, borderBottom: "1px solid #eee" }}
        bodySx={{ background: "#f8fafc" }}
        // icon={<GradingOutlined className="thin" />}
      >
        <AddToLoanItems onSelect={onSelect} />
      </DrilldownSidebarPane>
    </>
  );
});

const ADD_TO_LOAN_ITEMS = [
  {
    category: "People & Entities",
    items: [
      {
        key: loanDrilldownSidebarTypes.ADD_BORROWER,
        label: "Borrower",
        description: "Add a new borrower to the loan",
        icon: PersonAddIcon,
      },
      {
        key: loanDrilldownSidebarTypes.ADD_LOAN_RELATED_CONTACT,
        label: "Related Contact",
        description: "Link a contact to this loan",
        icon: PeopleIcon,
      },
      {
        key: loanDrilldownSidebarTypes.ADD_EDIT_ENTITY,
        label: "Entity",
        description: "Add or edit a business entity",
        icon: BusinessIcon,
      },
      // {
      //   key: loanDrilldownSidebarTypes.ADD_EDIT_ENTITY_MEMBER,
      //   label: "Entity Member",
      //   description: "Add a member to an entity",
      //   icon: PersonAddAltIcon,
      // },
    ],
  },
  {
    category: "Property & Collateral",
    items: [
      {
        key: loanDrilldownSidebarTypes.ADD_PROPERTY,
        label: "Property",
        description: "Add a collateral property",
        icon: HomeIcon,
      },
    ],
  },
  {
    category: "Fees & Financials",
    items: [
      {
        key: loanDrilldownSidebarTypes.ADD_LOAN_FEE,
        label: "Loan Fee",
        description: "Add a fee to the loan",
        icon: AttachMoneyIcon,
      },
      {
        key: loanDrilldownSidebarTypes.ADD_LOAN_CREDIT,
        label: "Loan Credit",
        description: "Apply a credit to the loan",
        icon: CreditCardIcon,
      },
      {
        key: loanDrilldownSidebarTypes.ADD_EDIT_CORRESPONDENT_FEE,
        label: "Correspondent Fee",
        description: "Add or edit correspondent fees",
        icon: InventoryIcon,
      },
      {
        key: loanDrilldownSidebarTypes.ADD_EDIT_THIRD_PARTY_FEES,
        label: "Third Party Fee",
        description: "Add or edit third-party fees",
        icon: ReceiptIcon,
      },
      {
        key: loanDrilldownSidebarTypes.ADD_LOAN_ESCROW_ITEM,
        label: "Escrow Item",
        description: "Add an escrow reserve item",
        icon: BookmarkIcon,
      },
    ],
  },
  {
    category: "Terms & Conditions",
    items: [
      {
        key: loanDrilldownSidebarTypes.ADD_EXCEPTION,
        label: "Exception",
        description: "Request a loan exception",
        icon: WarningIcon,
      },
      {
        key: loanDrilldownSidebarTypes.ADD_EDIT_LOAN_CONDITION,
        label: "Loan Condition",
        description: "Add or modify loan conditions",
        icon: CheckBoxIcon,
      },
      // {
      //   key: loanDrilldownSidebarTypes.ADD_LOAN_SPECIAL_CONDITION,
      //   label: "Special Condition",
      //   description: "Add a special loan condition",
      //   icon: InfoIcon,
      // },
      {
        key: loanDrilldownSidebarTypes.ADD_EDIT_BROKER_TERMS,
        label: "Broker Terms",
        description: "Add or edit broker agreement terms",
        icon: DescriptionIcon,
      },
    ],
  },
];

const AddToLoanItemCard = memo(({ item, onSelect }) => {
  const Icon = item.icon;

  return (
    <Card
      variant="outlined"
      sx={{
        p: 0,
        borderColor: "grey.300",
        transition: "all 0.2s ease",
        "&:hover": {
          borderColor: "primary.main",
          boxShadow: "0 2px 8px rgba(25, 118, 210, 0.12)",
          "& .item-icon-box": {
            bgcolor: "primary.main",
            color: "white",
          },
          "& .item-chevron": {
            color: "primary.main",
          },
        },
      }}
    >
      <CardActionArea
        onClick={() => onSelect(item.key)}
        sx={{
          p: 1.75,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          justifyContent: "flex-start",
        }}
      >
        <Box
          className="item-icon-box"
          sx={{
            width: 40,
            height: 40,
            borderRadius: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "grey.100",
            color: "grey.600",
            flexShrink: 0,
            transition: "all 0.2s ease",
          }}
        >
          <Icon fontSize="small" />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600} color="text.primary">
            {item.label}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ lineHeight: 1.3, fontSize: "11px" }}
          >
            {item.description}
          </Typography>
        </Box>
        <ChevronRightIcon
          className="item-chevron"
          sx={{
            fontSize: 20,
            color: "grey.400",
            flexShrink: 0,
            transition: "color 0.2s ease",
          }}
        />
      </CardActionArea>
    </Card>
  );
});

const AddToLoanItems = ({ onSelect }) => {
  const [search, setSearch] = useState("");

  const handleSelect = useCallback(
    (key) => {
      onSelect?.(key);
      // e.g. dispatch(setDrilldownSidebar({ type: loanDrilldownSidebarTypes[key] }))
    },
    [onSelect],
  );

  const filteredCategories = useMemo(
    () =>
      ADD_TO_LOAN_ITEMS.map((cat) => ({
        ...cat,
        items: cat.items.filter(
          (item) =>
            item.label.toLowerCase().includes(search.toLowerCase()) ||
            item.description.toLowerCase().includes(search.toLowerCase()),
        ),
      })).filter((cat) => cat.items.length > 0),
    [search],
  );

  return (
    <Box>
      <TextField
        size="small"
        fullWidth
        placeholder="Search items..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search sx={{ fontSize: 18, color: "grey.500" }} />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2 }}
      />

      {filteredCategories.length === 0 && (
        <Box sx={{ textAlign: "center", py: 6, color: "text.secondary" }}>
          <Search sx={{ fontSize: 36, color: "grey.400", mb: 1 }} />
          <Typography variant="body2">No matching items found</Typography>
        </Box>
      )}

      {filteredCategories.map((category) => (
        <Box key={category.category} sx={{ mb: 2.5 }}>
          <Typography
            variant="overline"
            sx={{
              color: "text.secondary",
              px: 0.25,
              mb: 1,
              display: "block",
              letterSpacing: 1,
            }}
          >
            {category.category}
          </Typography>
          <FlxGrid width="200px">
            {category.items.map((item) => (
              <AddToLoanItemCard
                key={item.key}
                item={item}
                onSelect={handleSelect}
              />
            ))}
          </FlxGrid>
        </Box>
      ))}
    </Box>
  );
};

export default DrilldownCreateNewItemSidebar;
